import { useState, type FormEvent } from "react";
import { Phone, Mail, MapPin, Zap, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link } from "@/lib/router-compat";
import FadeIn from "@/components/FadeIn";
import SEOHead from "@/components/SEOHead";
import { submitQuoteRequest } from "@/lib/quoteRequest";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { formatPhone, formatWeight, validateLeadForm } from "@/components/QuoteModal";
import { trackEvent } from "@/lib/analytics";

const Contact = () => {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [startedAt] = useState(Date.now());
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const emptyForm = {
    originCity: "", originState: "", destCity: "", destState: "",
    freightType: "", weight: "", commodity: "",
    name: "", company: "", email: "", phone: "",
    website: "",
  };
  const [form, setForm] = useState(emptyForm);

  const update = (k: keyof typeof emptyForm, v: string) => {
    const formatted = k === "phone"
      ? formatPhone(v)
      : k === "weight"
        ? formatWeight(v)
        : k === "originState" || k === "destState"
          ? v.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 3)
          : v;
    setForm((p) => ({ ...p, [k]: formatted }));
    setErrors((p) => ({ ...p, [k]: undefined }));
  };

  const fieldError = (field: keyof typeof emptyForm) => errors[field] ? <p className="mt-1 text-xs font-medium text-destructive">{errors[field]}</p> : null;

  const handleNext = () => {
    const nextErrors = validateLeadForm(form, "quote", step);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length === 0) setStep(step + 1);
    if (Object.keys(nextErrors).length === 0) trackEvent("quote_step_completed", { step, form_type: "quote", cta_source: "contact_page_quote" });
  };

  const handleSubmit = async () => {
    const nextErrors = validateLeadForm(form, "quote");
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setIsSubmitting(true);
    const payload = {
      ...form,
      name: form.name.trim(),
      company: form.company.trim(),
      email: form.email.trim().toLowerCase(),
      originCity: form.originCity.trim(),
      originState: form.originState.trim().toUpperCase(),
      destCity: form.destCity.trim(),
      destState: form.destState.trim().toUpperCase(),
      commodity: form.commodity.trim(),
      requestType: "quote",
      ctaSource: "contact_page_quote",
      pagePath: "/contact",
      startedAt,
    };

    const { error } = await submitQuoteRequest(payload);
    setIsSubmitting(false);

    if (error) {
      trackEvent("lead_submit_error", { form_type: "quote", cta_source: "contact_page_quote" });
      toast.error("We couldn't submit your quote request. Please call (855) 226-7123.");
      return;
    }

    trackEvent("lead_submit_success", { form_type: "quote", cta_source: "contact_page_quote" });
    toast.success("Thanks! We'll quote this as quickly as possible.");
    setStep(1);
    setErrors({});
    setForm(emptyForm);
  };

  const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (step < 3) {
      handleNext();
      return;
    }
    void handleSubmit();
  };

  return (
    <>
      <SEOHead
        title="Contact Varstar Alliance | Get a Freight Quote"
        description="Call (855) 226-7123 or request a free quote. Detroit-based freight brokerage serving US & Canada. Real people, real answers."
        canonical="/contact"
      >
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "@id": "https://varstaralliance.com/#logisticsservice",
          "name": "Varstar Alliance",
          "telephone": "+18552267123",
          "email": "info@varstaralliance.com",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "400 Renaissance Center, Suite 2600",
            "addressLocality": "Detroit",
            "addressRegion": "MI",
            "postalCode": "48243",
            "addressCountry": "US"
          },
          "geo": { "@type": "GeoCoordinates", "latitude": 42.3294, "longitude": -83.04 },
          "url": "https://varstaralliance.com",
          "openingHours": "Mo-Fr 08:00-18:00",
          "priceRange": "$$",
          "description": "Asset-based freight brokerage and logistics company serving the US and Canada."
        })}</script>
      </SEOHead>

      {/* Hero */}
      <section className="bg-foreground noise-texture pt-32 pb-20">
        <div className="container mx-auto px-4 text-center max-w-3xl">
          <FadeIn>
            <span className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-full bg-white/[0.06] border border-white/[0.08]">
              <span className="w-1.5 h-1.5 rounded-full bg-electric animate-pulse" />
              <span className="text-sm text-white/70 font-semibold">Available 24/7</span>
            </span>
            <h1 className="secondary-page-title text-white mb-6">Let's talk freight.</h1>
            <p className="text-lg text-white/70">
              Whether you need a quote, have a question, or want to explore a partnership — we're here. Real people, real answers, real fast.
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="py-20 bg-background">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">

            {/* Contact Info */}
            <FadeIn>
              <div className="space-y-4">
                {[
                  { icon: Phone, label: "(855) 226-7123", sub: "24/7 for active shipments.", href: "tel:+18552267123", color: "text-electric", bg: "bg-electric/10" },
                  { icon: Mail, label: "info@varstaralliance.com", sub: "", href: "mailto:info@varstaralliance.com", color: "text-emerald-500", bg: "bg-emerald-500/10" },
                  { icon: MapPin, label: "400 Renaissance Center, Suite 2600, Detroit, MI 48243", sub: "Headquarters", href: "", color: "text-amber-500", bg: "bg-amber-500/10" },
                ].map((c, i) => (
                  <div key={i} className="border border-border rounded-xl p-5 flex items-center gap-4 bg-card hover:shadow-xs transition-shadow">
                    <div className={`w-10 h-10 rounded-xl ${c.bg} flex items-center justify-center flex-shrink-0`}>
                      <c.icon className={`w-5 h-5 ${c.color}`} />
                    </div>
                    <div>
                      {c.href ? (
                        <a href={c.href} className="font-bold text-foreground hover:text-electric transition-colors">{c.label}</a>
                      ) : (
                        <p className="font-bold text-foreground">{c.label}</p>
                      )}
                      {c.sub && <p className="text-muted-foreground text-sm">{c.sub}</p>}
                    </div>
                  </div>
                ))}

                <div className="border border-border rounded-xl overflow-hidden h-64 bg-card flex items-center justify-center">
                  <div className="text-center text-muted-foreground">
                    <MapPin className="w-8 h-8 mx-auto mb-2 text-electric/40" />
                    <p className="text-sm font-medium text-foreground">Detroit, MI headquarters</p>
                    <p className="text-xs text-muted-foreground mt-1">Freight coverage across the US and Canada</p>
                  </div>
                </div>
              </div>
            </FadeIn>

            {/* Quote Form */}
            <FadeIn delay={0.2}>
              <form className="border border-border rounded-2xl p-6 lg:p-8 bg-card" onSubmit={handleFormSubmit} noValidate aria-labelledby="contact-quote-heading">
                <h2 id="contact-quote-heading" className="compact-heading text-foreground mb-2">Request a Quote</h2>

                {/* Step bar */}
                <div className="flex gap-1 mb-4">
                  {[1, 2, 3].map((s) => (
                    <div
                      key={s}
                      className={`h-1.5 flex-1 rounded-full transition-colors ${s <= step ? "bg-electric" : "bg-border"}`}
                    />
                  ))}
                </div>
                <p className="text-sm text-muted-foreground mb-6">Step {step} of 3</p>

                {step === 1 && (
                  <div className="space-y-4" role="group" aria-label="Origin and destination">
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label htmlFor="c-origin-city">Origin City *</Label><Input id="c-origin-city" autoComplete="address-level2" required aria-required="true" value={form.originCity} onChange={(e) => update("originCity", e.target.value)} placeholder="Detroit" aria-invalid={!!errors['originCity']} />{fieldError("originCity")}</div>
                      <div><Label htmlFor="c-origin-state">Origin State *</Label><Input id="c-origin-state" autoComplete="address-level1" required aria-required="true" value={form.originState} onChange={(e) => update("originState", e.target.value)} placeholder="MI" aria-invalid={!!errors['originState']} />{fieldError("originState")}</div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label htmlFor="c-dest-city">Destination City *</Label><Input id="c-dest-city" required aria-required="true" value={form.destCity} onChange={(e) => update("destCity", e.target.value)} placeholder="Toronto" aria-invalid={!!errors['destCity']} />{fieldError("destCity")}</div>
                      <div><Label htmlFor="c-dest-state">Destination State/Province *</Label><Input id="c-dest-state" required aria-required="true" value={form.destState} onChange={(e) => update("destState", e.target.value)} placeholder="ON" aria-invalid={!!errors['destState']} />{fieldError("destState")}</div>
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div className="space-y-4" role="group" aria-label="Freight details">
                    <div>
                      <Label htmlFor="c-freight-type">Freight Type *</Label>
                      <Select value={form.freightType} onValueChange={(v) => update("freightType", v)}>
                        <SelectTrigger id="c-freight-type" aria-required="true" aria-invalid={!!errors['freightType']}><SelectValue placeholder="Select type" /></SelectTrigger>
                        <SelectContent>
                          {["FTL", "LTL", "Reefer", "Flatbed", "Intermodal", "Other"].map((t) => (
                            <SelectItem key={t} value={t}>{t}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {fieldError("freightType")}
                    </div>
                    <div><Label htmlFor="c-weight">Weight (lbs) *</Label><Input id="c-weight" inputMode="numeric" required aria-required="true" value={form.weight} onChange={(e) => update("weight", e.target.value)} placeholder="10,000" aria-invalid={!!errors['weight']} />{fieldError("weight")}</div>
                    <div><Label htmlFor="c-commodity">Commodity *</Label><Input id="c-commodity" required aria-required="true" value={form.commodity} onChange={(e) => update("commodity", e.target.value)} placeholder="Auto parts" aria-invalid={!!errors['commodity']} />{fieldError("commodity")}</div>
                  </div>
                )}

                {step === 3 && (
                  <div className="space-y-4" role="group" aria-label="Contact information">
                    <div><Label htmlFor="c-name">Name *</Label><Input id="c-name" autoComplete="name" required aria-required="true" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="John Smith" aria-invalid={!!errors['name']} />{fieldError("name")}</div>
                    <div><Label htmlFor="c-company">Company *</Label><Input id="c-company" autoComplete="organization" required aria-required="true" value={form.company} onChange={(e) => update("company", e.target.value)} placeholder="Acme Corp" aria-invalid={!!errors['company']} />{fieldError("company")}</div>
                    <div><Label htmlFor="c-email">Email *</Label><Input id="c-email" type="email" autoComplete="email" required aria-required="true" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="john@acme.com" aria-invalid={!!errors['email']} />{fieldError("email")}</div>
                    <div><Label htmlFor="c-phone">Phone *</Label><Input id="c-phone" type="tel" autoComplete="tel" required aria-required="true" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="(555) 123-4567" aria-invalid={!!errors['phone']} />{fieldError("phone")}</div>
                  </div>
                )}

                <div className="flex justify-between mt-6">
                  {step > 1 ? (
                    <Button type="button" variant="outline" onClick={() => setStep(step - 1)}>Back</Button>
                  ) : <div />}
                  {step < 3 ? (
                    <Button
                      type="submit"
                      className="bg-electric text-white hover:bg-electric/90 rounded-full font-semibold px-6"
                      onClick={handleNext}
                    >
                      Next
                    </Button>
                  ) : (
                    <Button
                      type="submit"
                      className="bg-electric text-white hover:bg-electric/90 rounded-full font-semibold px-6"
                      disabled={isSubmitting}
                      onClick={handleSubmit}
                    >
                      {isSubmitting ? "Submitting..." : "Get My Free Quote"}
                    </Button>
                  )}
                </div>

                <p className="text-xs text-muted-foreground text-center mt-4 flex items-center justify-center gap-1">
                  <Zap className="w-3 h-3 text-electric" /> We respond to every request as quickly as possible.
                </p>
              </form>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Secondary CTAs */}
      <section className="bg-card border-t border-border py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <FadeIn>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Link
                to="/carriers"
                className="group border border-border rounded-2xl p-8 bg-background hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
              >
                <h3 className="text-lg font-bold text-foreground mb-2">Looking to haul?</h3>
                <p className="text-muted-foreground text-sm mb-4">Quality loads, quick pay, and a partner who respects your time.</p>
                <span className="inline-flex items-center gap-2 text-sm font-semibold text-electric group-hover:gap-3 transition-all">
                  Start Hauling <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
              <Link
                to="/agents"
                className="group border border-border rounded-2xl p-8 bg-background hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
              >
                <h3 className="text-lg font-bold text-foreground mb-2">Are you a freight agent?</h3>
                <p className="text-muted-foreground text-sm mb-4">Keep your book. Get better tech, better splits, better support.</p>
                <span className="inline-flex items-center gap-2 text-sm font-semibold text-electric group-hover:gap-3 transition-all">
                  Become an Agent <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>
    </>
  );
};

export default Contact;
